import React from 'react';
import { ArrowUpRight, ArrowLeft, Calendar } from 'lucide-react';
import { ONGOING_TOURS, UPCOMING_TOURS, COMPLETED_TOURS } from '../constants';

interface TourDate {
  date: string;
  state: string;
  location: string;
}

interface TourCardProps {
  tour: { 
    id: string; 
    image: string;
    title?: string;
    dates?: TourDate[];
  };
  actionLabel?: string;
  onAction?: () => void;
}

const TourCard: React.FC<TourCardProps> = ({ tour, actionLabel, onAction }) => {
  return (
    <div className="group relative border border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/30 rounded-sm overflow-hidden hover:border-primary/50 transition-all hover:shadow-[0_0_20px_rgba(249,115,22,0.15)] flex flex-col h-full">
      
      {/* Image Section - Modified to fit the well (contain) */}
      <div className="w-full relative aspect-[3/4] bg-black/20 dark:bg-black/40 shrink-0">
        <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-colors z-10 pointer-events-none"></div>
        <img 
          src={tour.image} 
          alt="Tour Visual" 
          className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
        />
        
        {/* Overlay CTA Button */}
        {actionLabel && onAction && (
            <div className="absolute bottom-6 left-0 right-0 flex justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-2 group-hover:translate-y-0">
                <button 
                    onClick={(e) => { e.stopPropagation(); onAction(); }}
                    className="bg-primary hover:bg-orange-600 text-white font-mono font-bold text-xs py-2 px-6 uppercase tracking-widest shadow-lg hover:shadow-orange-500/50 transition-all flex items-center gap-2"
                >
                    {actionLabel}
                    <ArrowUpRight size={14} />
                </button>
            </div>
        )}
      </div>

      {/* Tour Dates Section */}
      {tour.dates && tour.dates.length > 0 && (
        <div className="flex-grow flex flex-col border-t border-slate-200/50 dark:border-slate-800/50 bg-white dark:bg-slate-900/80 p-4">
            <div className="flex items-center gap-2 mb-4">
                <Calendar size={16} className="text-primary" />
                <h3 className="text-sm font-bold font-display uppercase tracking-wider text-slate-900 dark:text-white">
                    {tour.title || 'Tour Dates'}
                </h3>
            </div>
            <div className="overflow-y-auto max-h-[300px] pr-2 space-y-3 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700">
                {tour.dates.map((d, i) => (
                    <div key={i} className="flex flex-col border-b border-slate-100 dark:border-slate-800/60 pb-3 last:border-0 last:pb-0">
                        <span className="text-xs font-mono font-bold text-primary mb-1">{d.date}</span>
                        <div className="flex items-start justify-between gap-2">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-tight">{d.location}</span>
                            <span className="text-xs font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-sm shrink-0">{d.state}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      )}

    </div>
  );
};

interface ToursPageProps {
  onNavigate: (page: string) => void;
}

const ToursPage: React.FC<ToursPageProps> = ({ onNavigate }) => {
  const auroraStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(to right, hsla(228, 51%, 70%, 1) 0%, hsla(27, 90%, 68%, 1) 45%, hsla(29, 100%, 49%, 1) 50%, hsla(27, 90%, 68%, 1) 55%, hsla(228, 51%, 70%, 1) 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  const handleSubmission = () => onNavigate('submission');

  return (
    <section className="px-6 pt-0 pb-12 md:pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto">
        
        {/* Back Button */}
        <button 
          onClick={() => onNavigate('home')}
          className="mb-8 flex items-center gap-2 text-slate-500 hover:text-primary transition-colors font-mono text-[10px] uppercase tracking-[0.2em] group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>

        {/* Page Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl md:text-6xl font-bold uppercase font-display tracking-tighter mb-2 animate-aurora"
            style={auroraStyle}
          >
            Tours
          </h1>
        </div>

        {/* Upcoming Tours Section */}
        <div className="mb-20">
            <div className="flex items-center gap-4 mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(234,179,8,0.6)]"></div>
                <h2 className="text-xl md:text-2xl font-bold uppercase font-display text-slate-900 dark:text-white tracking-tight">
                    Upcoming Tours
                </h2>
                <button 
                    onClick={handleSubmission}
                    className="ml-2 px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-widest border border-primary text-primary hover:bg-primary hover:text-white transition-colors rounded-sm"
                >
                    Apply
                </button>
            </div>
            
            {/* Grid Layout for Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {UPCOMING_TOURS.map(tour => (
                    <TourCard 
                        key={tour.id} 
                        tour={tour} 
                        actionLabel="Apply Now"
                        onAction={handleSubmission}
                    />
                ))}
            </div>
        </div>

        {/* Ongoing Tours Section */}
        <div className="mb-20">
            <div className="flex items-center gap-4 mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.6)]"></div>
                <h2 className="text-xl md:text-2xl font-bold uppercase font-display text-slate-900 dark:text-white tracking-tight">
                    Ongoing Tours
                </h2>
                <button 
                    onClick={handleSubmission}
                    className="ml-2 px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-widest border border-primary text-primary hover:bg-primary hover:text-white transition-colors rounded-sm"
                >
                    Join
                </button>
            </div>
            
            {/* Grid Layout for Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {ONGOING_TOURS.map(tour => (
                    <TourCard 
                        key={tour.id} 
                        tour={tour} 
                    />
                ))}
            </div>
        </div>

        {/* Completed Tours Section */}
        <div className="mb-20">
            <div className="flex items-center gap-4 mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
                <div className="w-2 h-2 bg-slate-500 rounded-full shadow-[0_0_10px_rgba(100,116,139,0.6)]"></div>
                <h2 className="text-xl md:text-2xl font-bold uppercase font-display text-slate-900 dark:text-white tracking-tight">
                    Completed Tours
                </h2>
            </div>
            
            {/* Grid Layout for Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {COMPLETED_TOURS.map(tour => (
                    <TourCard 
                        key={tour.id} 
                        tour={tour} 
                    />
                ))}
            </div>
        </div>

      </div>
    </section>
  );
};

export default ToursPage;